import React, {useContext, useEffect, useState} from "react"
import {firebase, firestore, storage, database} from "../firebase"
import {useAuth} from "./AuthContext";

const GroupContext = React.createContext({})

export function useGroup() {
    return useContext(GroupContext)
}

export function GroupProvider({children}) {
    const [group, setGroup] = useState()
    const [isV2, setIsV2] = useState(false)
    const [dolphinEnabled, setDolphinEnabled] = useState(false)
    const [name, setName] = useState()
    const [email, setEmail] = useState()
    const [role, setRole] = useState()
    const [selectedBot, setSelectedBot] = useState("")
    const [selectedSerial, setSelectedSerial] = useState()
    const [botNames, setBotNames] = useState([])
    const [botSerials, setBotSerials] = useState([])
    const [locationDocs, setLocationDocs] = useState([])
    const [videoNames, setVideoNames] = useState([])
    const [connectedStatuses, setConnectedStatuses] = useState([]);

    const {currentUser} = useAuth()

    // Set of context wide pending state (didn't use state because set functions
    // from state are not guaranteed to change the value of the underlying state
    // variable within the scope of the function that calls them
    var pending = false

    const setPending = (newValue) => {
        pending = newValue
    }

    const getPending = () => {
        return pending
    }

    //Allow command to be sent to a bot other than the selected if desired
    //  e.g. in GoalComponent pause/play/cancel it doesn't make sense to press within a row and not act on that agent
    const sendUserInput = (utterance, listener) => {
        if (listener === undefined) {
            listener = selectedBot
        }

        if (connectedStatuses[selectedSerial] === "offline" || listener === "") {
            alert("selected agent to send command is offline - please try again after reconnecting or select another agent")
            return false
        }

        //If a command from another sendUserInput is still pending, wait
        if (getPending()) {
            alert("Your last command is still processing - please try again after it is finished!")
            return false
        }
        //Comment back in for convenient debugging of all NL pipeline commands
        //console.log(`Sending input ${utterance} to ${listener}`)
        //mark this command as pending both context-wide and locally
        setPending(true)
        var pendingLocal = true
        const promiseDocRef = firestore.collection(`groups/${group}/userInput`).add({
            utterance: utterance,
            speaker: name,
            listener: listener,
            time: new Date().getTime()
        })
        //Called every time the userInput entry is updated
        promiseDocRef.then((ref) => {
            const unsub = ref.onSnapshot((doc) => {
                //if this userInput entry is deleted, update both pendingValues
                if (!doc.exists) {
                    setPending(false)
                    pendingLocal = false
                    unsub()
                }
            });
            setTimeout(() => {
                //Todo: Will: Do we want to unsub in this case as well?
                //If the local message (I.E. the command sent by this call of sendUserInput)
                //is still pending after 5 seconds, tell the user there's a delay. This is
                //not checking the context-wide pending, as this can be set true by other
                //asynchronous calls to sendUserInput, and we only care if one individual
                //command takes more than 5 seconds to be processed
                if (pendingLocal) {
                    alert("Your command is taking a very long time to process - please check your computer's and Zeeno's internet connectivity!")
                }
            }, 5000);
        })
        return true
    }

    //The variables and functions that will be available in this context
    const value = {
        group,
        name,
        email,
        role,
        locationDocs,
        videoNames,
        botNames,
        botSerials,
        selectedBot,
        selectedSerial,
        setName,
        setGroup,
        setSelectedBot,
        setSelectedSerial,
        setBotSerials,
        setBotNames,
        connectedStatuses,
        sendUserInput,
        setVideoNames,
        isV2,
        dolphinEnabled
    }

    //Current User effect (sets user data for the context)
    useEffect(() => {
        if (currentUser !== null && currentUser !== undefined) {
            //Fetches the user's data from the users collection on firebase
            const fetchUserData = async () => {
                const data = await firestore.collection("users").where("email", "==", currentUser.email).get();
                if(data.docs.length === 0) {
                    await firebase.auth().currentUser.getIdToken(true)
                } else {
                    return data.docs[0].data()
                }
            }

            fetchUserData().then(r => {
                if(r) {
                    setName(r.name)
                    setGroup(r.group)
                    setRole(r.role)
                    setEmail(r.email)
                }
            })
        }
    }, [currentUser])

    useEffect(() => {
        firestore.collection(`v2`).doc(`${group}`).get().then((doc) => {
            if (doc.exists) {
                setIsV2(true);
            }
        });
    }, [group])

    useEffect(() => {
        firestore.collection(`groups`).doc(`${group}`).get().then((doc) => {
            if (doc.exists && doc.data().client_endpoint !== undefined) {
                setDolphinEnabled(true);
            } else {
                setDolphinEnabled(false);
            }
        });
    }, [group])

    //Group effect (sets listeners for group dependent queries)
    useEffect(() => {
        //Generates listeners on the group's location collection
        let currentLocUnsubscriber;
        const generateLocationsListener = async () => {
            //Set the listener to dispatch on added message. Return unsubscriber
            currentLocUnsubscriber = await firestore.collection(`groups/${group}/navigation`).onSnapshot(snapshot => {
                if (snapshot !== undefined && snapshot.docs !== undefined) {
                    setLocationDocs(snapshot.docs)
                }
            });
        }
        
        //Call locationListener generator to subscribe and also set unsubscriber
        generateLocationsListener()

        //New - Make user need to refresh to get new bots to show up, don't want this to trigger more than once
        //  (was causing things to reload when there was just an update to a subfield of a certain agent)
        const generateBotListener = async () => {
            firestore.collection(`groups/${group}/robots`).get().then(snapshot => {
                let currentBots = [];
                let currentSerials = [];
                snapshot.docs.forEach( doc => {
                    currentBots.push(doc.data().name);
                    currentSerials.push(doc.id);
                })

                setBotSerials(currentSerials);
                setBotNames(currentBots);

                setSelectedBot(currentBots[0]);
                setSelectedSerial(currentSerials[0]);
            });
        }

        //Call locationListener generator to subscribe and also set unsubscriber
        generateLocationsListener()

        //Call botListener generator to subscribe and also set unsubscriber
        generateBotListener()

        //Fetches the list of current videos in the storage bucket for this group
        //TODO: Will: Any subscription like way to make this update dynamically
        //when the user adds a video?
        const storageRef = storage.ref();
        if (group !== undefined) {
            const videosRef = storageRef.child(`${group}/videos`);
            const videos = []
            videosRef.listAll()
                .then((res) => {
                    res.items.forEach((itemRef) => {
                        videos.push(itemRef.name);
                    });
                    setVideoNames(videos)
                });
        }

        //Cleanup effect
        return () => {
            currentLocUnsubscriber()
        }
    }, [group])

    //EW: Originally in GoalComponent, feel like it makes sense for the connection status of all agents be group wide
    //      info.
    useEffect(() => {

        const updateConnectedStatuses = () => {
           let tempCons = {}
           botSerials.forEach(serial => {
               database.ref(group + "/status/" + serial + "/state").once('value', (snapshot) => {
                    tempCons[serial] = snapshot.val()
                });
            });
            setConnectedStatuses(tempCons)
        }

        //TODO: definitely better way to do this
        //TODO: do we need to call 'off' on each of these?
        //on update to any agent's online status, update all agents' statuses to display
        let dbUnsubscribers = {}
        botSerials.forEach((serial) => {
            const onChange = database.ref(group + "/status/" + serial + "/state").on('value', (snapshot) => {
                if (snapshot !== undefined) {
                    updateConnectedStatuses()
                }
            });
            dbUnsubscribers[serial] = onChange
        });

        return () => {
            botSerials.forEach((serial) => {
                database.ref(group + "/status/" + serial + "/state").off('value', dbUnsubscribers[serial])
            })
        }

    }, [group, botSerials])

    return (
        <GroupContext.Provider value={value}>
            {children}
        </GroupContext.Provider>
    )

}
